import React, { useEffect, useState } from "react";
import * as d3 from "d3";
import { TacticsData, TacticScale, Tactics, PopupData } from "../types/data";
import {
  gamePrediction,
  tactical_analysis,
  team_configs,
} from "../router/resources/data";

import { getMetricString } from "../router/resources/utils";

interface Props {
  tableData: TacticsData;
  defaultData: TacticsData;
  changeSetOpen: (val: boolean) => void;
  handleMetric: (val: Tactics) => void;
  handleIsHome: (val: boolean) => void;
  handleIsEfficiency: (val: boolean) => void;
  handleValue: (val: number) => void;
  handleLocks?: (isHome: boolean, isUsage: boolean, tactic: Tactics) => void;
  handlePopupData: (val: PopupData) => void;
  homeTeam: number;
  awayTeam: number;
  scales: TacticScale;
  mode: number;
  lockChange?: boolean
}

const tacticList: Tactics[] = [
  "isolation",
  "transition",
  "off ball screen inside",
  "off ball screen outside",
  "on ball screen inside",
  "on ball screen outside",
];

/* This component is the comparison of the teams */
const TeamTable: React.FC<Props> = ({
  changeSetOpen,
  tableData,
  defaultData,
  handleMetric,
  handleValue,
  handleIsHome,
  handleIsEfficiency,
  handlePopupData,
  handleLocks,
  scales,
  homeTeam,
  awayTeam,
  mode,
  lockChange,
}) => {
  const getPopupData = async (
    isHome: boolean,
    isEfficiency: boolean,
    homeTeam: number,
    awayTeam: number,
    metric: Tactics
  ) => {
    const data = await tactical_analysis({
      team: isHome ? homeTeam : awayTeam,
      side: isHome ? "home" : "away",
      tactical_situation: getMetricString(metric),
      type: isEfficiency ? "efficiency" : "usage",
    });
    handlePopupData(data);
  };

  const handleCellClick = (
    value: number,
    tactic: Tactics,
    isHome: boolean,
    isEfficiency: boolean
  ) => {
    handleMetric(tactic);
    handleValue(value);
    handleIsHome(isHome);
    handleIsEfficiency(isEfficiency);
    getPopupData(isHome, isEfficiency, homeTeam, awayTeam, tactic).then(() =>
      changeSetOpen(true)
    );
  };

  // Adding a scale for each tactic we have.
  const colorScales = {
    transition: {
      efficiency: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales.transition.efficiency.min,
          scales.transition.efficiency.max,
        ]),
      usage: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([scales.transition.usage.min, scales.transition.usage.max]),
    },
    isolation: {
      efficiency: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales.isolation.efficiency.min,
          scales.transition.efficiency.max,
        ]),
      usage: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([scales.isolation.usage.min, scales.transition.usage.max]),
    },
    "off ball screen inside": {
      efficiency: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["off ball screen inside"].efficiency.min,
          scales.transition.efficiency.max,
        ]),
      usage: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["off ball screen inside"].usage.min,
          scales.transition.usage.max,
        ]),
    },
    "off ball screen outside": {
      efficiency: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["off ball screen outside"].efficiency.min,
          scales.transition.efficiency.max,
        ]),
      usage: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["off ball screen outside"].usage.min,
          scales.transition.usage.max,
        ]),
    },
    "on ball screen inside": {
      efficiency: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["on ball screen inside"].efficiency.min,
          scales.transition.efficiency.max,
        ]),
      usage: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["on ball screen inside"].usage.min,
          scales.transition.usage.max,
        ]),
    },
    "on ball screen outside": {
      efficiency: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["on ball screen outside"].efficiency.min,
          scales.transition.efficiency.max,
        ]),
      usage: d3
        .scaleSequential(d3.interpolateRgbBasis(["white", "#c82121"]))
        .domain([
          scales["on ball screen outside"].usage.min,
          scales.transition.usage.max,
        ]),
    },
  };

  const [clickedLock, setClickedLock] = useState<boolean>(false);


  return (
    <div className="container mx-auto my-2 text-sm" style={{ width: "fit-content" }}>
      <table className="table-auto">
        <thead>
          <tr>
            <th className="border px-4 py-4 text-center" rowSpan={2}>
              Tactical Situation
            </th>
            <th className="border px-4 py-4 text-center" colSpan={2}>
              Efficiency
            </th>
            <th className="border px-4 py-4 text-center" colSpan={2}>
              Usage
            </th>
          </tr>
          <tr>
            <th className="border px-4 py-4 text-center">Home Team</th>
            <th className="border px-4 py-4 text-center">Away Team</th>
            <th className="border px-4 py-4 text-center">Home Team</th>
            <th className="border px-4 py-4 text-center">Away Team</th>
          </tr>
        </thead>
        <tbody>
          {tacticList.map((item, index) => (
            <tr key={tacticList[index]}>
              <td
                key={`${tacticList[index]}_home_efficiency`}
                className="border px-4 py-4 text-center font-bold"
              >
                {tacticList[index].split(" ").map((word) => word[0].toUpperCase() + word.slice(1)).join(" ")}
              </td>
              <td
                key={0}
                className="border px-4 py-4 text-center cursor-pointer transform transition-transform duration-200 hover:scale-105 relative"
                style={{
                  backgroundColor:
                    tableData.home[tacticList[index]].efficiency ===
                    defaultData.home[tacticList[index]].efficiency
                      ? colorScales[tacticList[index]].efficiency(
                          Number(
                            tableData.home[
                              tacticList[index]
                            ].efficiency.toFixed(2)
                          )
                        )
                      : "#A3A3A3",
                      pointerEvents: clickedLock ? 'none' : 'auto'
                }}
                onClick={() => {
                  console.log('jjjjjjj')
                  handleCellClick(
                    tableData.home[tacticList[index]].efficiency,
                    tacticList[index],
                    true,
                    true
                  );
                }}
              >
                {mode === 1 && tableData.home[tacticList[index]].lock![0] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(true, false, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {mode === 1 && !tableData.home[tacticList[index]].lock![0] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(true, false, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 14v3m4-6V7a3 3 0 1 1 6 0v4M5 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {tableData.home[tacticList[index]].efficiency.toFixed(2)}
              </td>
              <td
                key={`${tacticList[index]}_away_efficiency`}
                className="border px-4 py-4 text-center cursor-pointer transform transition-transform duration-200 hover:scale-105"
                style={{
                  backgroundColor:
                    tableData.away[tacticList[index]].efficiency ===
                    defaultData.away[tacticList[index]].efficiency
                      ? colorScales[tacticList[index]].efficiency(
                          Number(
                            tableData.away[
                              tacticList[index]
                            ].efficiency.toFixed(2)
                          )
                        )
                      : "#A3A3A3",
                }}
                onClick={() =>
                  handleCellClick(
                    tableData.away[tacticList[index]].efficiency,
                    tacticList[index],
                    false,
                    true
                  )
                }
              >
                {mode === 1 && tableData.away[tacticList[index]].lock![0] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(false, false, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {mode === 1 && !tableData.away[tacticList[index]].lock![0] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(false, false, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 14v3m4-6V7a3 3 0 1 1 6 0v4M5 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {tableData.away[tacticList[index]].efficiency.toFixed(2)}
              </td>
              <td
                key={`${tacticList[index]}_home_usage`}
                className="border px-4 py-4 text-center cursor-pointer transform transition-transform duration-200 hover:scale-105"
                style={{
                  backgroundColor:
                    tableData.home[tacticList[index]].usage ===
                    defaultData.home[tacticList[index]].usage
                      ? colorScales[tacticList[index]].usage(
                          Number(
                            tableData.home[tacticList[index]].usage.toFixed(2)
                          )
                        )
                      : "#A3A3A3",
                }}
                onClick={() =>
                  handleCellClick(
                    tableData.home[tacticList[index]].usage,
                    tacticList[index],
                    true,
                    false
                  )
                }
              >
                {mode === 1 && tableData.home[tacticList[index]].lock![1] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(true, true, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {mode === 1 && !tableData.home[tacticList[index]].lock![1] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(true, true, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 14v3m4-6V7a3 3 0 1 1 6 0v4M5 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {tableData.home[tacticList[index]].usage.toFixed(2)}
              </td>
              <td
                key={`${tacticList[index]}_away_usage`}
                className="border px-4 py-4 text-center cursor-pointer transform transition-transform duration-200 hover:scale-105"
                style={{
                  backgroundColor:
                    tableData.away[tacticList[index]].usage ===
                    defaultData.away[tacticList[index]].usage
                      ? colorScales[tacticList[index]].usage(
                          Number(
                            tableData.away[tacticList[index]].usage.toFixed(2)
                          )
                        )
                      : "#A3A3A3",
                }}
                onClick={() =>
                  handleCellClick(
                    tableData.away[tacticList[index]].usage,
                    tacticList[index],
                    false,
                    false
                  )
                }
              >
                {mode === 1 && tableData.away[tacticList[index]].lock![1] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(false, true, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 14v3m-3-6V7a3 3 0 1 1 6 0v4m-8 0h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {mode === 1 && !tableData.away[tacticList[index]].lock![1] && (
                  <button
                    className="flex items-center absolute top-0 right-0 m-2 justify-center border rounded-md hover:bg-neutral-400"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      event.stopPropagation();
                      setClickedLock(true);
                      handleLocks!(false, true, tacticList[index]);
                      setTimeout(() => {
                        setClickedLock(false);
                      }, 0);
                    }}
                  >
                    <svg
                      className="w-5 h-5 text-gray-800 dark:text-white"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M10 14v3m4-6V7a3 3 0 1 1 6 0v4M5 11h10a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1Z"
                      />
                    </svg>
                  </button>
                )}
                {tableData.away[tacticList[index]].usage.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TeamTable;
